import {
  TruckJourneyDelivery,
  TruckJourneyDeliveryEdit,
  TruckJourneyLoad,
  TruckJourneyLoadEdit,
  TruckJourneyStop,
  TruckJourneyStopEdit
} from '../models/truck-journey.model';
import { StopType } from '../../../shared/stop-type.model';

export class TruckJourneyLoadMapper {

  public static fromLoadtoLoadEdit(load: TruckJourneyLoad): TruckJourneyLoadEdit {
    const loadEdit = new TruckJourneyLoadEdit();
    const deliveriesEdit: TruckJourneyDeliveryEdit[] = [];
    load.stops
      .flatMap(stop => stop.deliveries.map(delivery => ({
        deliveryNumber: delivery.number,
        salesOrderNumber: delivery.salesOrderNumber,
        stopType: delivery.stopType,
        stopEdit: this.fromStopToStopEdit(stop)
      })))
      .forEach(aggregatedDelivery => {
        const mappedDelivery = deliveriesEdit
          .find(delivery => aggregatedDelivery.deliveryNumber === delivery.deliveryNumber);

        if (mappedDelivery) {
          if (aggregatedDelivery.stopType === StopType.Drop) {
            mappedDelivery.dropStop = aggregatedDelivery.stopEdit;
          } else if (aggregatedDelivery.stopType === StopType.Pick) {
            mappedDelivery.pickStop = aggregatedDelivery.stopEdit;
          }
        } else {
          if (aggregatedDelivery.stopType === StopType.Pick) {
            deliveriesEdit.push({
              deliveryNumber: aggregatedDelivery.deliveryNumber,
              salesOrderNumber: aggregatedDelivery.salesOrderNumber,
              pickStop: aggregatedDelivery.stopEdit,
              dropStop: undefined
            });
          } else if (aggregatedDelivery.stopType === StopType.Drop) {
            deliveriesEdit.push({
              deliveryNumber: aggregatedDelivery.deliveryNumber,
              salesOrderNumber: aggregatedDelivery.salesOrderNumber,
              dropStop: aggregatedDelivery.stopEdit,
              pickStop: undefined
            });
          }
        }
      });

    loadEdit.loadId = load.loadId;
    loadEdit.deliveryNumbers = load.deliveryNumbers;
    loadEdit.totalStops = load.stops.length;
    loadEdit.deliveries = deliveriesEdit;
    return loadEdit;
  }

  static fromStopToStopEdit(stop: TruckJourneyStop): TruckJourneyStopEdit {
    const stopEdit = new TruckJourneyStopEdit();
    stopEdit.stopId = stop.stopId;
    stopEdit.locationLabel = stop.locationLabel;
    stopEdit.locationType = stop.locationType;
    stopEdit.plannedArrival = stop.plannedArrival;
    stopEdit.plannedDeparture = stop.plannedDeparture;
    return stopEdit;
  }

  public static fromDeliveriesEditToStops(deliveries: TruckJourneyDeliveryEdit[]): TruckJourneyStop[] {
    const newStops = deliveries.flatMap(delivery => [
      this.fromDeliveryToStopOfType(delivery.deliveryNumber, delivery.salesOrderNumber, delivery.pickStop, StopType.Pick),
      this.fromDeliveryToStopOfType(delivery.deliveryNumber, delivery.salesOrderNumber, delivery.dropStop, StopType.Drop)
    ]);
    return newStops?.length <= 2 ? newStops : this.mergeConsecutiveStops(this.mergeConsecutivePickStops(newStops));
  }

  /**
   * The goal here is to merge back the deliveries having the same stop of type pick together if they are consecutive.
   *
   * @param stops reordered stops
   */
  private static mergeConsecutivePickStops(stops: TruckJourneyStop[]): TruckJourneyStop[] {
    const result: TruckJourneyStop[] = [];
    let i = 0;

    while (i < stops.length) {
      const currentStop = stops[i];
      // Only attempt to merge Pick stops
      if (currentStop.deliveries.every(d => d.stopType === StopType.Pick)) {
        const mergedDeliveries = [...currentStop.deliveries];
        let nextPickIndex = i + 1;
        // Look ahead to find the next Pick stop with the same stopId
        while (nextPickIndex < stops.length) {
          const nextPickStop = stops[nextPickIndex];
          // If we find a Pick stop with a different stopId, we stop merging
          if (nextPickStop.deliveries.some(d => d.stopType === StopType.Pick) && nextPickStop.stopId !== currentStop.stopId) {
            break;
          }
          // If we find a Pick stop with the same stopId, merge its deliveries
          if (nextPickStop.stopId === currentStop.stopId && nextPickStop.deliveries.every(d => d.stopType === StopType.Pick)) {
            mergedDeliveries.push(...nextPickStop.deliveries);
            stops.splice(nextPickIndex, 1); // Remove the merged stop from the original list
            continue;
          }
          nextPickIndex++;
        }
        currentStop.deliveries = mergedDeliveries;
      }
      currentStop.deliveries = this.ensurePickedDeliveriesAreAfterDroppedDeliveries(currentStop.deliveries);
      result.push(currentStop);
      i++;
    }
    return result;
  }

  private static ensurePickedDeliveriesAreAfterDroppedDeliveries(deliveries: TruckJourneyDelivery[]): TruckJourneyDelivery[] {
    return deliveries.sort((d1, d2) =>
      d1.stopType === StopType.Pick ? 1 : d2.stopType === StopType.Pick ? -1 : 0
    );
  }

  /**
   * Once we merge the pick stops together we look to merge the delvieries having the same consecutive stop ids.
   *
   * @param stops
   */
  private static mergeConsecutiveStops(stops: TruckJourneyStop[]): TruckJourneyStop[] {
    return stops.reduce((acc: TruckJourneyStop[], currentStop) => {
      if (acc.length > 0) {
        const lastStop = acc[acc.length - 1];
        if (lastStop.stopId === currentStop.stopId) {
          lastStop.deliveries.push(...currentStop.deliveries);
          return acc;
        }
      }
      acc.push(currentStop);
      return acc;
    }, []);
  }

  static fromDeliveryToStopOfType(deliveryNumber: string, salesOrderNumber: string, deliveryStop: TruckJourneyStopEdit, type: StopType): TruckJourneyStop {
    const stop = new TruckJourneyStop();
    stop.stopId = deliveryStop.stopId;
    stop.glid = deliveryStop.glid;
    stop.locationLabel = deliveryStop.locationLabel;
    stop.locationType = deliveryStop.locationType;
    stop.plannedArrival = deliveryStop.plannedArrival;
    stop.plannedDeparture = deliveryStop.plannedDeparture;
    stop.deliveries = [{number: deliveryNumber, stopType: type, salesOrderNumber}];
    return stop;
  }
}
